import { MatterTreatmentAppointmentsCard } from "@/components/features/matter/medical-treatments/MatterTreatmentAppointmentsCard/MatterTreatmentAppointmentsCard";
import { MatterOverviewTreatmentPlansCard } from "@/components/features/matter/overview/info-cards/MatterOverviewTreatmentPlansCard";
import {
  Button,
  Card,
  CardHeader,
  ServerErrorFallbackCard,
  Text,
} from "@/components/ui";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { useUser } from "@/components/UserProvider";
import {
  sortTreatmentsByEarliestAppointment,
  useMedicalTreatmentViewModel,
} from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { useGlobalModal, useHandleSearchParams } from "@/hooks/useGlobalModal";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { ErrorBoundary } from "@sentry/react";
import { Clock, List, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { MedicalTreatmentProviderCard } from "./MedicalTreatmentProviderCard";

interface MedicalTreatmentsProps {
  matter: Matter;
  searchMedicalTreatmentId?: string;
  searchAppointmentId?: string;
  searchBillId?: string;
  searchRecordId?: string;
  searchPlanId?: string;
}

type MatterWithMedicalTreatmentCollections = Partial<Matter> & {
  id?: string;
  medicalTreatmentBills?: Matter["medicalTreatmentBills"];
  medicalTreatments?: Matter["medicalTreatments"];
};

const EMPTY_MEDICAL_TREATMENT_BILLS: Matter["medicalTreatmentBills"] = [];
const EMPTY_MEDICAL_TREATMENTS: Matter["medicalTreatments"] = [];

function MedicalTreatmentsDangerous({
  matter,
  searchMedicalTreatmentId,
  searchAppointmentId,
  searchBillId,
  searchRecordId,
  searchPlanId,
}: MedicalTreatmentsProps) {
  const { hasPagePermission, hasActionPermission } = useUser();
  const hasViewPermission = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailMedicalTreatments,
  );
  const { openMedicalTreatmentModal } = useGlobalModal();
  const {
    handleMedicalTreatmentSearch,
    handleTreatmentAppointmentSearch,
    handleTreatmentBillSearch,
    handleTreatmentRecordSearch,
  } = useHandleSearchParams();
  const completeMedicalTreatments = useMedicalTreatmentViewModel(matter);
  const matterCollections = matter as MatterWithMedicalTreatmentCollections;
  const medicalTreatmentBills =
    matterCollections.medicalTreatmentBills ?? EMPTY_MEDICAL_TREATMENT_BILLS;
  const medicalTreatments =
    matterCollections.medicalTreatments ?? EMPTY_MEDICAL_TREATMENTS;
  const matterId = matterCollections.id ?? matter.id;

  const sortedTreatmentsByNextAppointment = useMemo(() => {
    return sortTreatmentsByEarliestAppointment(completeMedicalTreatments);
  }, [completeMedicalTreatments]);

  const chronology = useMemo(() => {
    return completeMedicalTreatments.flatMap((treatment) =>
      treatment.appointments.map((appointment) => ({
        ...appointment,
        provider: treatment.provider,
        type: treatment.type,
      })),
    );
  }, [completeMedicalTreatments]);

  const [selectedTab, setSelectedTab] = useState<
    "providers" | "timeline" | "treatment-plan"
  >("providers");

  const { totalBill, totalPaid } = calculateMedicalTreatmentBillTotals(
    medicalTreatmentBills,
  );

  const lienBills = useMemo(
    () =>
      medicalTreatmentBills.filter(
        (bill) => bill.lien === "Lien Filed" || bill.lien === "Lien Signed",
      ),
    [medicalTreatmentBills],
  );
  const nonLienBills = useMemo(
    () =>
      medicalTreatmentBills.filter(
        (bill) => bill.lien !== "Lien Filed" && bill.lien !== "Lien Signed",
      ),
    [medicalTreatmentBills],
  );
  const {
    totalOutstanding: lienOutstanding,
    totalBill: lienTotalBill,
    totalPaid: lienTotalPaid,
  } = calculateMedicalTreatmentBillTotals(lienBills);
  const {
    totalOutstanding: billOutstanding,
    totalBill: billTotalBill,
    totalPaid: billTotalPaid,
  } = calculateMedicalTreatmentBillTotals(nonLienBills);
  const totalOutstandingExposure = billOutstanding + lienOutstanding;

  const processedSearches = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!searchMedicalTreatmentId) return;
    const searchKey = `medical-treatment-${searchMedicalTreatmentId}`;
    if (processedSearches.current.has(searchKey)) return;
    processedSearches.current.add(searchKey);
    handleMedicalTreatmentSearch(matter, searchMedicalTreatmentId);
  }, [searchMedicalTreatmentId, matter, handleMedicalTreatmentSearch]);

  useEffect(() => {
    if (!searchAppointmentId) return;
    const searchKey = `appointment-${searchAppointmentId}`;
    if (processedSearches.current.has(searchKey)) return;
    processedSearches.current.add(searchKey);
    handleTreatmentAppointmentSearch(matter, searchAppointmentId);
  }, [searchAppointmentId, matter, handleTreatmentAppointmentSearch]);

  useEffect(() => {
    if (!searchBillId) return;
    const searchKey = `bill-${searchBillId}`;
    if (processedSearches.current.has(searchKey)) return;
    processedSearches.current.add(searchKey);
    handleTreatmentBillSearch(matter, searchBillId);
  }, [searchBillId, matter, handleTreatmentBillSearch]);

  useEffect(() => {
    if (!searchRecordId) return;
    const searchKey = `record-${searchRecordId}`;
    if (processedSearches.current.has(searchKey)) return;
    processedSearches.current.add(searchKey);
    handleTreatmentRecordSearch(matter, searchRecordId);
  }, [searchRecordId, matter, handleTreatmentRecordSearch]);

  useEffect(() => {
    if (!searchPlanId) return;
    const searchKey = `plan-${searchPlanId}`;
    if (processedSearches.current.has(searchKey)) return;
    processedSearches.current.add(searchKey);
    setSelectedTab("treatment-plan");
  }, [searchPlanId]);

  return (
    <div className="flex flex-col gap-y-4 p-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <Text variant="title">Medical treatments</Text>
        {hasActionPermission(
          ACTION_PERMISSION_KEYS.matterMedicalTreatment,
          ACTION_PERMISSION_VALUES.create,
        ) && (
          <Button
            onClick={() => {
              openMedicalTreatmentModal({
                matterId: matter.id,
                editing: false,
                initialTab: "details",
              });
            }}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Add treatment
          </Button>
        )}
      </div>

      {/* Tabs */}
      <nav className="border-border flex items-center gap-6 border-b">
        {(
          [
            {
              value: "providers",
              label: "Treatments",
              icon: <List className="h-3.5 w-3.5" />,
            },
            {
              value: "treatment-plan",
              label: "Treatment plan",
              icon: <Plus className="h-3.5 w-3.5" />,
            },
            {
              value: "timeline",
              label: "Timeline",
              icon: <Clock className="h-3.5 w-3.5" />,
            },
          ] as const
        ).map(({ value, label, icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setSelectedTab(value);
            }}
            className={cn(
              "-mb-px flex items-center gap-2 border-b-2 pb-3 transition-colors",
              selectedTab === value ? "border-primary" : "border-transparent",
            )}
          >
            {icon}
            <Text
              variant={selectedTab === value ? "body" : "subHeading"}
              as="span"
              className={cn(
                selectedTab === value
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </Text>
          </button>
        ))}
      </nav>

      {/* Summary cards */}
      {hasViewPermission && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="border-border bg-card rounded-lg border p-4">
            <Text variant="caption" color="muted" className="font-medium">
              Total billed
            </Text>
            <Text variant="title" className="mt-1 text-2xl leading-8">
              <CurrencyWrapper value={totalBill} />
            </Text>
            <div className="mt-2 space-y-1">
              <div className="flex items-center justify-between gap-3">
                <Text variant="caption" color="muted" as="span">
                  Bills
                </Text>
                <Text variant="caption" as="span" className="font-medium">
                  <CurrencyWrapper value={billTotalBill} />
                </Text>
              </div>
              <div className="flex items-center justify-between gap-3">
                <Text variant="caption" color="muted" as="span">
                  Liens
                </Text>
                <Text variant="caption" as="span" className="font-medium">
                  <CurrencyWrapper value={lienTotalBill} />
                </Text>
              </div>
            </div>
          </div>
          <div className="border-border bg-card rounded-lg border p-4">
            <Text variant="caption" color="muted" className="font-medium">
              Total paid
            </Text>
            <Text variant="title" className="mt-1 text-2xl leading-8">
              <CurrencyWrapper value={totalPaid} />
            </Text>
            <div className="mt-2 space-y-1">
              <div className="flex items-center justify-between gap-3">
                <Text variant="caption" color="muted" as="span">
                  Bills
                </Text>
                <Text variant="caption" as="span" className="font-medium">
                  <CurrencyWrapper value={billTotalPaid} />
                </Text>
              </div>
              <div className="flex items-center justify-between gap-3">
                <Text variant="caption" color="muted" as="span">
                  Liens
                </Text>
                <Text variant="caption" as="span" className="font-medium">
                  <CurrencyWrapper value={lienTotalPaid} />
                </Text>
              </div>
            </div>
          </div>
          <div className="border-border bg-card rounded-lg border p-4">
            <Text variant="caption" color="muted" className="font-medium">
              Total outstanding
            </Text>
            <Text variant="title" className="mt-1 text-2xl leading-8">
              <CurrencyWrapper value={totalOutstandingExposure} />
            </Text>
            <div className="mt-2 space-y-1">
              <div className="flex items-center justify-between gap-3">
                <Text variant="caption" color="muted" as="span">
                  Bills
                </Text>
                <Text variant="caption" as="span" className="font-medium">
                  <CurrencyWrapper value={billOutstanding} />
                </Text>
              </div>
              <div className="flex items-center justify-between gap-3">
                <Text variant="caption" color="muted" as="span">
                  Liens
                </Text>
                <Text variant="caption" as="span" className="font-medium">
                  <CurrencyWrapper value={lienOutstanding} />
                </Text>
              </div>
            </div>
          </div>
        </div>
      )}

      {!hasViewPermission && (
        <Card className="w-full border-2">
          <CardHeader className="space-y-1">
            <Text variant="subHeading">
              You do not have permission to view medical treatments.
            </Text>
          </CardHeader>
        </Card>
      )}

      {hasViewPermission && (
        <>
          {medicalTreatments.length === 0 ? (
            <Card className="w-full border-2 border-dashed">
              <CardHeader className="space-y-1">
                <Text
                  variant="body"
                  className="text-muted-foreground text-center"
                >
                  {"You haven't added a treatment yet."}
                </Text>
                <Text variant="subHeading" className="text-center">
                  {"Create your first by clicking 'Add treatment'"}
                </Text>
              </CardHeader>
            </Card>
          ) : selectedTab === "providers" ? (
            sortedTreatmentsByNextAppointment.map((treatment) => (
              <MedicalTreatmentProviderCard
                key={treatment.id}
                treatment={treatment}
                matter={matter}
              />
            ))
          ) : selectedTab === "timeline" ? (
            <Card className="border-border w-full p-6">
              <MatterTreatmentAppointmentsCard
                matterId={matterId}
                appointments={chronology}
                expanded={"appointments"}
                showProvider
              />
            </Card>
          ) : (
            <Card className="border-border w-full p-6 pt-2">
              <MatterOverviewTreatmentPlansCard
                initialPlanId={searchPlanId}
                matter={matter}
                showHeader={false}
                showCreateButton={hasActionPermission(
                  ACTION_PERMISSION_KEYS.matterMedicalTreatment,
                  ACTION_PERMISSION_VALUES.create,
                )}
                sortOrder="desc"
                variant="chronology"
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default function MedicalTreatments(props: MedicalTreatmentsProps) {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-6">
          <ServerErrorFallbackCard />
        </div>
      }
    >
      <MedicalTreatmentsDangerous {...props} />
    </ErrorBoundary>
  );
}
